/**
 * Pure selection logic for the PRATUS AI data-source picker, lifted out of the
 * legacy `DataSources.js` so the V2 `DataSourcePicker` can reuse it without the
 * legacy panel chrome. The `RealtimeSelection[]` shape is unchanged — it is
 * exactly what is posted to `/disasterchat`.
 */
import dayjs from 'dayjs';
import {
  ChatWeatherDataSources,
  DChatDataSources,
  LayersDataSource,
  LocationRequiredDataSources,
  SeeristDataSource,
  TakEntitiesDataSource,
  sourcePrefixes,
} from '../constants/dataSourceConstants';
import {
  DEFAULT_SEERIST_REQUEST,
  seeristRequestLabel,
} from '../SeeristRequestBuilder/seeristRequestConstants';
import { getLayersAiMapScopeLabel } from '../utils/addSelectedLayerForAI';
import type { SelectedLayerForAI } from '../utils/prefetchLayerFeaturesForAI';
import type { RealtimeSelection, WebDateRange } from './types';

export type { WebDateRange };

export interface DataSourceOption {
  source: string;
  description?: string;
  id: string;
  isCustom?: boolean;
  customData?: Record<string, unknown> & { id?: string };
}

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

/** Default Web window: end = now, start = five days earlier. */
export function defaultWebDateRange(now: Date = new Date()): WebDateRange {
  return {
    start: new Date(now.getTime() - FIVE_DAYS_MS).toISOString(),
    end: now.toISOString(),
  };
}

/**
 * Default PRATUS AI sources when the drawer opens on the hub with an empty
 * session. High-security orgs hide Web from the picker, so it is omitted
 * there.
 */
export function defaultHubRealtimeSelections(
  highSecurity: boolean
): RealtimeSelection[] {
  const weather: RealtimeSelection[] = [
    { source: 'Current Weather' },
    { source: 'Forecast Weather' },
  ];
  return highSecurity
    ? weather
    : [{ source: 'Web', webDateRange: defaultWebDateRange() }, ...weather];
}

export interface CustomDataSourceRow {
  id: string;
  name: string;
  description?: string;
  [key: string]: unknown;
}

export interface BuildSourcesFlags {
  usingSitrep?: boolean;
  isUsingTeams?: boolean;
  highSecurity?: boolean;
  tennesseeNationalGuard?: boolean;
  neo?: boolean;
  customDataSources?: CustomDataSourceRow[];
}

/** Same catalog and ordering as the legacy panel. */
export const buildAvailableSources = ({
  usingSitrep = false,
  isUsingTeams = false,
  highSecurity = false,
  tennesseeNationalGuard = false,
  neo = false,
  customDataSources = [],
}: BuildSourcesFlags): DataSourceOption[] => {
  let sources: DataSourceOption[] = [
    { source: 'SITREP', description: 'PRATUS AI SITREPS', id: '' },
    {
      source: 'Files',
      description: `Files uploaded to Pratus${isUsingTeams ? ' and SharePoint' : ''}`,
      id: '',
    },
  ];
  if (!highSecurity) {
    sources.push({ source: 'Web', description: 'Web Scraping', id: '' });
  }

  // Exercise is being reconfigured and has no agent tool yet.
  const dchatSources = DChatDataSources.filter((s) => s.source !== 'Exercise');
  const locationRequired = tennesseeNationalGuard
    ? LocationRequiredDataSources
    : LocationRequiredDataSources.filter((s) => s.source !== 'TAK Entities');

  if (usingSitrep) {
    sources = sources.filter((s) => s.source !== 'SITREP');
    sources = [
      ...sources,
      ...locationRequired,
      LayersDataSource,
      ...dchatSources,
    ];
  } else {
    sources = [
      ...sources,
      ...ChatWeatherDataSources,
      LayersDataSource,
      SeeristDataSource,
      ...(tennesseeNationalGuard ? [TakEntitiesDataSource] : []),
      ...dchatSources,
    ];
  }

  const custom: DataSourceOption[] = customDataSources.map((cd) => ({
    source: cd.name,
    description: cd.description,
    id: cd.id,
    isCustom: true,
    customData: cd,
  }));
  sources = [...sources, ...custom];

  if (neo) {
    sources.push({
      source: 'NEO Analysis',
      description: 'Noncombatant Evacuation Order Analysis.',
      id: '',
    });
  }
  return sources;
};

export const filterSources = (
  sources: DataSourceOption[],
  term: string
): DataSourceOption[] => {
  const q = term.trim().toLowerCase();
  if (!q) return sources;
  return sources.filter(
    (s) =>
      s.source.toLowerCase().includes(q) ||
      (s.description ?? '').toLowerCase().includes(q)
  );
};

export const isSourceSelected = (
  selections: RealtimeSelection[],
  source: string
): boolean => selections.some((s) => s.source === source);

export const findSelection = (
  selections: RealtimeSelection[],
  source: string
): RealtimeSelection | undefined => selections.find((s) => s.source === source);

/** Sources whose configuration opens a dedicated tab after selection. */
export type ConfigTab = 'files' | 'layers' | 'seerist' | 'web' | null;

export interface ToggleContext {
  currentIncidentId?: string;
  currentExerciseId?: string;
}

export interface ToggleResult {
  next: RealtimeSelection[];
  /** Tab to open so the user can finish configuring the source. */
  openTab: ConfigTab;
}

export const toggleSource = (
  selections: RealtimeSelection[],
  source: DataSourceOption,
  ctx: ToggleContext = {}
): ToggleResult => {
  if (isSourceSelected(selections, source.source)) {
    return {
      next: selections.filter((s) => s.source !== source.source),
      openTab: null,
    };
  }
  let id = source.id || '';
  if (source.source === 'Incident' && ctx.currentIncidentId) {
    id = ctx.currentIncidentId;
  } else if (source.source === 'Exercise' && ctx.currentExerciseId) {
    id = ctx.currentExerciseId;
  }
  const nextSelection: RealtimeSelection = {
    source: source.source,
    id,
    customData: source.customData,
  };
  if (source.source === 'Seerist') {
    nextSelection.seeristRequest = DEFAULT_SEERIST_REQUEST as Record<
      string,
      unknown
    >;
  }
  if (source.source === 'Web') {
    nextSelection.webDateRange = defaultWebDateRange();
  }
  const openTab: ConfigTab =
    source.source === 'Files'
      ? 'files'
      : source.source === 'Layers'
        ? 'layers'
        : source.source === 'Seerist'
          ? 'seerist'
          : source.source === 'Web'
            ? 'web'
            : null;
  return { next: [...selections, nextSelection], openTab };
};

export const setSourceId = (
  selections: RealtimeSelection[],
  source: string,
  id: string | null
): RealtimeSelection[] => {
  const rest = selections.filter((s) => s.source !== source);
  return id ? [...rest, { source, id }] : rest;
};

const upsert = (
  selections: RealtimeSelection[],
  source: string,
  patch: Partial<RealtimeSelection>,
  create: () => RealtimeSelection
): RealtimeSelection[] =>
  isSourceSelected(selections, source)
    ? selections.map((s) => (s.source === source ? { ...s, ...patch } : s))
    : [...selections, create()];

export const setSelectedFiles = (
  selections: RealtimeSelection[],
  fileIds: string[]
): RealtimeSelection[] =>
  selections.map((s) =>
    s.source === 'Files' ? { ...s, selectedFiles: fileIds } : s
  );

export const setSelectedLayers = (
  selections: RealtimeSelection[],
  selectedLayers: SelectedLayerForAI[]
): RealtimeSelection[] =>
  upsert(selections, 'Layers', { selectedLayers }, () => ({
    source: 'Layers',
    id: '',
    selectedLayers,
  }));

export const setLayersFilterByDatetime = (
  selections: RealtimeSelection[],
  enabled: boolean
): RealtimeSelection[] =>
  upsert(selections, 'Layers', { filterByDatetime: enabled }, () => ({
    source: 'Layers',
    id: '',
    selectedLayers: [],
    filterByDatetime: enabled,
  }));

export const setSeeristRequest = (
  selections: RealtimeSelection[],
  seeristRequest: Record<string, unknown>
): RealtimeSelection[] =>
  upsert(selections, 'Seerist', { seeristRequest }, () => ({
    source: 'Seerist',
    id: '',
    seeristRequest,
  }));

export const getSelectedFileIds = (selections: RealtimeSelection[]): string[] =>
  findSelection(selections, 'Files')?.selectedFiles ?? [];

export const getSelectedLayers = (
  selections: RealtimeSelection[]
): SelectedLayerForAI[] =>
  findSelection(selections, 'Layers')?.selectedLayers ?? [];

/** Default off unless the saved selection already carries a datetime window. */
export const getLayersFilterByDatetime = (
  selections: RealtimeSelection[]
): boolean => {
  const layers = findSelection(selections, 'Layers');
  if (!layers) return false;
  if (layers.filterByDatetime === true) return true;
  if (layers.filterByDatetime === false) return false;
  return (layers.selectedLayers ?? []).some(
    (l) => !!l?.timeExtent || !!l?.timeFilterLabel
  );
};

export const getSeeristRequest = (
  selections: RealtimeSelection[]
): Record<string, unknown> =>
  (findSelection(selections, 'Seerist')?.seeristRequest as
    | Record<string, unknown>
    | undefined) ?? (DEFAULT_SEERIST_REQUEST as Record<string, unknown>);

export const getWebDateRange = (
  selections: RealtimeSelection[]
): WebDateRange | null => {
  const range = findSelection(selections, 'Web')?.webDateRange;
  if (
    !range ||
    typeof range.start !== 'string' ||
    typeof range.end !== 'string' ||
    !range.start.trim() ||
    !range.end.trim()
  ) {
    return null;
  }
  return { start: range.start, end: range.end };
};

/** Null clears the window (unrestricted Web search). */
export const setWebDateRange = (
  selections: RealtimeSelection[],
  range: WebDateRange | null
): RealtimeSelection[] => {
  if (isSourceSelected(selections, 'Web')) {
    return selections.map((sel) => {
      if (sel.source !== 'Web') return sel;
      if (!range) {
        const { webDateRange: _removed, ...rest } = sel;
        return rest;
      }
      return { ...sel, webDateRange: range };
    });
  }
  if (!range) return selections;
  return [...selections, { source: 'Web', id: '', webDateRange: range }];
};

export function webDateRangeLabel(
  range: WebDateRange | null | undefined
): string | null {
  if (!range?.start || !range?.end) return null;
  const start = dayjs(range.start);
  const end = dayjs(range.end);
  if (!start.isValid() || !end.isValid()) return null;
  return `${start.format('MMM D')} – ${end.format('MMM D')}`;
}

export interface ConfigurationContext {
  usingSitrep?: boolean;
  currentIncidentId?: string;
  currentExerciseId?: string;
}

/**
 * Whether a source still needs a value before it is useful. Layers/Files only
 * "need config" once selected but empty, matching the legacy behavior.
 */
export const needsConfiguration = (
  selections: RealtimeSelection[],
  source: DataSourceOption,
  ctx: ConfigurationContext = {}
): boolean => {
  const sel = findSelection(selections, source.source);
  switch (source.source) {
    case 'SITREP':
      return !ctx.usingSitrep && !sel?.id;
    case 'NEO Analysis':
      return !sel?.id;
    case 'Incident':
      return !sel?.id && !ctx.currentIncidentId;
    case 'Exercise':
      return !sel?.id && !ctx.currentExerciseId;
    case 'Layers':
      return !!sel && getSelectedLayers(selections).length === 0;
    case 'Files':
      return !!sel && getSelectedFileIds(selections).length === 0;
    case 'Seerist':
      return !!sel && !sel.seeristRequest;
    default:
      return false;
  }
};

/** Chip count shown on the picker trigger ("3 sources"). */
export const selectionDisplayCount = (
  selections: RealtimeSelection[]
): number =>
  selections.reduce((n, sel) => {
    if (
      sel?.source === 'Layers' &&
      Array.isArray(sel.selectedLayers) &&
      sel.selectedLayers.length > 0
    ) {
      return n + sel.selectedLayers.length;
    }
    if (sel?.source === 'Files') {
      const ids = sel.selectedFiles;
      if (Array.isArray(ids) && ids.length > 0) return n + ids.length;
    }
    return n + 1;
  }, 0);

export interface DisplayNameLookups {
  sitrepNameById?: Record<string, string>;
  neoNameById?: Record<string, string>;
  incidentNameById?: Record<string, string>;
  exerciseNameById?: Record<string, string>;
  /** Time-filter label for an external layer id (from map context). */
  layerTimeLabelById?: Record<string, string | undefined>;
  /** Legend class label for a layer id (from map context). */
  layerLegendLabelById?: Record<string, string | undefined>;
}

export const displayNameFor = (
  selections: RealtimeSelection[],
  source: DataSourceOption | RealtimeSelection,
  lookups: DisplayNameLookups = {}
): string => {
  const name = source.source;
  const sel = findSelection(selections, name);
  const prefix = (sourcePrefixes as Record<string, string>)[name] ?? name;

  switch (name) {
    case 'SITREP': {
      const label = sel?.id ? lookups.sitrepNameById?.[sel.id] : undefined;
      return label ? `SITREP: ${label}` : prefix;
    }
    case 'NEO Analysis': {
      const label = sel?.id ? lookups.neoNameById?.[sel.id] : undefined;
      return label ? `NEO: ${label}` : prefix;
    }
    case 'Incident': {
      const label = sel?.id ? lookups.incidentNameById?.[sel.id] : undefined;
      return label ? `Incident: ${label}` : prefix;
    }
    case 'Exercise': {
      const label = sel?.id ? lookups.exerciseNameById?.[sel.id] : undefined;
      return label ? `Exercise: ${label}` : prefix;
    }
    case 'Files':
      return `Files (${getSelectedFileIds(selections).length} selected)`;
    case 'Layers': {
      const layers = getSelectedLayers(selections);
      const parts = [`Layers (${layers.length} selected)`];
      if (getLayersFilterByDatetime(selections)) {
        const time = layers
          .map((l) => l?.timeFilterLabel || lookups.layerTimeLabelById?.[l?.id])
          .find(Boolean);
        if (time) parts.push(`At ${time}`);
      }
      const legend = layers
        .map(
          (l) => l?.legendFilterLabel || lookups.layerLegendLabelById?.[l?.id]
        )
        .find(Boolean);
      if (legend) parts.push(legend);
      const scope = getLayersAiMapScopeLabel(selections);
      if (scope) parts.push(scope);
      return parts.join(' · ');
    }
    case 'Seerist':
      return seeristRequestLabel(sel?.seeristRequest as never);
    case 'Web': {
      const label = webDateRangeLabel(getWebDateRange(selections));
      return label ? `Web (${label})` : prefix;
    }
    default:
      return prefix;
  }
};

/** Keep the SITREP selection in step with the sitrep the user is viewing. */
export const syncSitrepSelection = (
  selections: RealtimeSelection[],
  currentSitrepId: string | null | undefined
): RealtimeSelection[] => {
  if (!currentSitrepId) return selections;
  const existing = findSelection(selections, 'SITREP');
  if (!existing)
    return [...selections, { source: 'SITREP', id: currentSitrepId }];
  if (existing.id !== currentSitrepId) {
    return selections.map((s) =>
      s.source === 'SITREP' ? { ...s, id: currentSitrepId } : s
    );
  }
  return selections;
};

/** Fill an id-less Incident/Exercise selection with the current workspace. */
export const syncWorkspaceSelection = (
  selections: RealtimeSelection[],
  source: 'Incident' | 'Exercise',
  currentId: string | undefined
): RealtimeSelection[] => {
  if (!currentId) return selections;
  const existing = findSelection(selections, source);
  if (!existing || existing.id) return selections;
  return selections.map((s) =>
    s.source === source ? { ...s, id: currentId } : s
  );
};
